import {
  AfterDateValueConfig,
  AfterDateValueConfigKind,
  AfterFilterValueConfig,
  BeforeDateValueConfig,
  BeforeDateValueConfigKind,
  BeforeFilterValueConfig,
  BetweenDateValueConfig,
  BetweenDateValueConfigKind,
  CohortFilterGranularity,
  FirstInteractionFilterConfigValues,
  InTheLastValueConfig,
  InTheLastValueConfigKind,
  LastInteractionFilterConfigValues,
} from '@/api/generated/model';
import { IFilterWhenFields, whenFieldsToConfig } from '@/components/filters/filter-when-forms';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { FILTERS } from '@/models/common/filters';
import { formatDateConfigs } from '@/utils/filter-utils';
import { translate } from '@/utils/translations';
import { humanizeString } from '@/utils/string.utils';

export type SpecialTimeFilterValues = FirstInteractionFilterConfigValues | LastInteractionFilterConfigValues;

export type SpecialTimeKind =
  InTheLastValueConfigKind | AfterDateValueConfigKind | BeforeDateValueConfigKind | BetweenDateValueConfigKind;

export type SpecialTimeFilterState = {
  fields: IFilterWhenFields;
  additionalFilter?: GenericFilter;
  preservedOffset?: number;
};

export const SPECIAL_TIME_KINDS: SpecialTimeKind[] = [
  InTheLastValueConfigKind.in_the_last,
  AfterDateValueConfigKind.after,
  BetweenDateValueConfigKind.between,
  BeforeDateValueConfigKind.before,
];

export const DEFAULT_SPECIAL_TIME_FIELDS: IFilterWhenFields = {
  kind: InTheLastValueConfigKind.in_the_last,
  number: 1,
  granularity: CohortFilterGranularity.day,
  pickedDate: null,
  pickedDates: null,
  filter: null,
};

export function isInteractionTimeFilter(column: string | null | undefined): boolean {
  return column === FILTERS.firstInteraction || column === FILTERS.lastInteraction;
}

function isGenericFilterValue(value: unknown): value is GenericFilter {
  return typeof value === 'object' && value !== null && 'column' in value && 'values' in value;
}

function parseAdditionalFilter(additionalFilter: unknown): GenericFilter | undefined {
  if (!additionalFilter || typeof additionalFilter !== 'object') return undefined;
  const filter = additionalFilter as GenericFilter;
  if (!filter.column) return undefined;
  return {
    column: filter.column,
    values: Array.isArray(filter.values) ? filter.values : [],
    is_exclude: filter.is_exclude ?? false,
    select_all: filter.select_all,
  };
}

function getPreservedOffset(values: SpecialTimeFilterValues): number | undefined {
  if ('offset' in values && typeof values.offset === 'number') {
    return values.offset;
  }
  return undefined;
}

export function parseSpecialTimeFilterValues(values: unknown): SpecialTimeFilterState {
  if (!values || typeof values !== 'object' || !('kind' in values)) {
    return { fields: { ...DEFAULT_SPECIAL_TIME_FIELDS } };
  }

  const config = values as SpecialTimeFilterValues;
  const preservedOffset = getPreservedOffset(config);
  const additionalFilter = parseAdditionalFilter('additional_filter' in config ? config.additional_filter : undefined);

  if (config.kind === InTheLastValueConfigKind.in_the_last) {
    const inTheLast = config as InTheLastValueConfig;
    return {
      fields: {
        kind: InTheLastValueConfigKind.in_the_last,
        number: inTheLast.value ?? 1,
        granularity: inTheLast.granularity ?? CohortFilterGranularity.day,
        pickedDate: null,
        pickedDates: null,
        filter: null,
      },
      additionalFilter,
      preservedOffset,
    };
  }

  if (config.kind === AfterDateValueConfigKind.after) {
    if (isGenericFilterValue(config.value)) {
      return {
        fields: {
          kind: AfterDateValueConfigKind.after,
          number: 0,
          granularity: CohortFilterGranularity.day,
          pickedDate: null,
          pickedDates: null,
          filter: config.value,
        },
        additionalFilter,
        preservedOffset,
      };
    }

    return {
      fields: {
        kind: AfterDateValueConfigKind.after,
        number: 0,
        granularity: CohortFilterGranularity.day,
        pickedDate: typeof config.value === 'string' ? new Date(config.value) : null,
        pickedDates: null,
        filter: null,
      },
      additionalFilter,
      preservedOffset,
    };
  }

  if (config.kind === BeforeDateValueConfigKind.before) {
    if (isGenericFilterValue(config.value)) {
      return {
        fields: {
          kind: BeforeDateValueConfigKind.before,
          number: 0,
          granularity: CohortFilterGranularity.day,
          pickedDate: null,
          pickedDates: null,
          filter: config.value,
        },
        additionalFilter,
        preservedOffset,
      };
    }

    return {
      fields: {
        kind: BeforeDateValueConfigKind.before,
        number: 0,
        granularity: CohortFilterGranularity.day,
        pickedDate: typeof config.value === 'string' ? new Date(config.value) : null,
        pickedDates: null,
        filter: null,
      },
      additionalFilter,
      preservedOffset,
    };
  }

  if (config.kind === BetweenDateValueConfigKind.between) {
    const between = config as BetweenDateValueConfig;
    const start = between.value?.start ? new Date(between.value.start) : null;
    const end = between.value?.end ? new Date(between.value.end) : null;
    return {
      fields: {
        kind: BetweenDateValueConfigKind.between,
        number: 0,
        granularity: CohortFilterGranularity.day,
        pickedDate: null,
        pickedDates: start && end ? [start, end] : null,
        filter: null,
      },
      additionalFilter,
      preservedOffset,
    };
  }

  return { fields: { ...DEFAULT_SPECIAL_TIME_FIELDS }, additionalFilter, preservedOffset };
}

export function serializeSpecialTimeFilterState(state: SpecialTimeFilterState): SpecialTimeFilterValues | null {
  const config = whenFieldsToConfig(state.fields, state.additionalFilter);
  if (!config) return null;

  if (state.preservedOffset !== undefined && 'offset' in config) {
    return { ...config, offset: state.preservedOffset } as SpecialTimeFilterValues;
  }

  return config as SpecialTimeFilterValues;
}

function withPreservedOffset(config: SpecialTimeFilterValues, preservedOffset?: number): SpecialTimeFilterValues {
  if (preservedOffset !== undefined && 'offset'in config) {
    return { ...config, offset: preservedOffset } as SpecialTimeFilterValues;
  }
  return config;
}

/** Always returns a valid config so kind selection can update the filter immediately. */
export function serializeSpecialTimeFilterStateForKindSelection(
  state: SpecialTimeFilterState,
): SpecialTimeFilterValues {
  const fullConfig = serializeSpecialTimeFilterState(state);
  if (fullConfig) return fullConfig;

  const { kind, pickedDate, pickedDates, filter } = state.fields;
  const now = new Date();
  const isoNow = now.toISOString();
  const additionalFilter = state.additionalFilter as InTheLastValueConfig['additional_filter'];

  if (kind === AfterDateValueConfigKind.after) {
    if (filter) {
      return withPreservedOffset(
        { kind, value: filter, additional_filter: additionalFilter } as AfterFilterValueConfig,
        state.preservedOffset,
      );
    }
    return withPreservedOffset(
      { kind, value: pickedDate?.toISOString() ?? isoNow, additional_filter: additionalFilter } as AfterDateValueConfig,
      state.preservedOffset,
    );
  }

  if (kind === BeforeDateValueConfigKind.before) {
    if (filter) {
      return withPreservedOffset(
        { kind, value: filter, additional_filter: additionalFilter } as BeforeFilterValueConfig,
        state.preservedOffset,
      );
    }
    return withPreservedOffset(
      {
        kind,
        value: pickedDate?.toISOString() ?? isoNow,
        additional_filter: additionalFilter,
      } as BeforeDateValueConfig,
      state.preservedOffset,
    );
  }

  if (kind === BetweenDateValueConfigKind.between) {
    const start = pickedDates?.[0] ?? now;
    const end = pickedDates?.[1] ?? now;
    return withPreservedOffset(
      {
        kind,
        value: { start: start.toISOString(), end: end.toISOString() },
        additional_filter: additionalFilter,
      } as BetweenDateValueConfig,
      state.preservedOffset,
    );
  }

  return createDefaultSpecialTimeFilterValues();
}

export function createDefaultSpecialTimeFilterValues(): SpecialTimeFilterValues {
  return serializeSpecialTimeFilterState({ fields: { ...DEFAULT_SPECIAL_TIME_FIELDS } })!;
}

export function getSpecialTimeKindFromValues(values: unknown): SpecialTimeKind | null {
  if (!values || typeof values !== 'object' || !('kind' in values)) return null;
  const kind = (values as { kind?: string }).kind;
  if (SPECIAL_TIME_KINDS.includes(kind as SpecialTimeKind)) {
    return kind as SpecialTimeKind;
  }
  return null;
}

export function getSpecialTimeKindLabel(kind: SpecialTimeKind): string {
  return translate('date_config_kind', kind);
}

function formatAdditionalFilterSummary(additionalFilter?: GenericFilter): string {
  if (!additionalFilter?.column) return '';

  const columnLabel = translate('common', additionalFilter.column) || humanizeString(additionalFilter.column);
  const values = Array.isArray(additionalFilter.values) ? additionalFilter.values : [];
  if (additionalFilter.select_all) {
    return `${columnLabel}: ALL`;
  }

  if (!values.length) {
    return `${columnLabel}`;
  }

  const formattedValues = values
    .map((value) => {
      const translated = translate(additionalFilter.column as keyof typeof translate, value);
      return translated === value ? humanizeString(value) : translated;
    })
    .join(', ');

  return `${columnLabel}: ${formattedValues}`;
}

/** Strip the leading date-kind label (e.g. "in the last") that formatDateConfigs
 * prepends, for callers that already show the kind separately as the operator. */
function stripDateKindLabel(summary: string, kind: SpecialTimeKind): string {
  const label = translate('date_config_kind', kind);
  if (label && summary.toLowerCase().startsWith(label.toLowerCase())) {
    return summary.slice(label.length).trimStart();
  }
  return summary;
}

export function formatSpecialTimePillSummary(values: unknown, options?: { omitKind?: boolean }): string | undefined {
  if (!values || typeof values !== 'object' || !('kind' in values)) return undefined;

  const config = values as SpecialTimeFilterValues;
  const fullSummary = formatDateConfigs(config);
  // When the kind is rendered separately (the operator segment), drop the kind
  // prefix so the chip reads "in the last 45 days" not "in the last in the last 45 days".
  const baseSummary = options?.omitKind ? stripDateKindLabel(fullSummary, config.kind as SpecialTimeKind) : fullSummary;
  const additionalFilter = parseAdditionalFilter('additional_filter' in config ? config.additional_filter : undefined);

  if (!additionalFilter?.column) {
    return baseSummary;
  }

  const nestedSummary = formatAdditionalFilterSummary(additionalFilter);
  if (!nestedSummary) {
    return `${baseSummary} & 1 sub-filter`;
  }

  const hasValues = Array.isArray(additionalFilter.values) && additionalFilter.values.length > 0;
  if (!hasValues) {
    return `${baseSummary} & 1 sub-filter`;
  }

  return `${baseSummary} & ${nestedSummary}`;
}

export function createSpecialTimeStateForKind(
  kind: SpecialTimeKind,
  previous?: SpecialTimeFilterState,
): SpecialTimeFilterState {
  const baseFields = previous?.fields ?? { ...DEFAULT_SPECIAL_TIME_FIELDS };

  return {
    fields: {
      kind,
      number: kind === InTheLastValueConfigKind.in_the_last ? baseFields.number || 1 : 0,
      granularity: baseFields.granularity ?? CohortFilterGranularity.day,
      pickedDate:
        kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before
          ? baseFields.pickedDate
          : null,
      pickedDates: kind === BetweenDateValueConfigKind.between ? baseFields.pickedDates : null,
      filter:
        kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before ? baseFields.filter : null,
    },
    additionalFilter:
      (kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before) && baseFields.filter
        ? undefined
        : previous?.additionalFilter,
    preservedOffset: previous?.preservedOffset,
  };
}
